import { Fragment, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { MarketplaceCampaignPostHistoryStatus, MarketplacePostHistory } from '@anytag/graphql';
import { Icon, MediaPreview } from '@anytag/shared-atoms';
import { AppLanguage } from '@anytag/shared-types';
import { cn, localizedDateFormatter } from '@anytag/shared-utils';
import { LinkifyItPreview } from '@anytag/shared-molecules';

interface HistorySectionProps {
  isDraftPost?: boolean;
  data?: readonly MarketplacePostHistory[] | null;
}

export const HistorySection = ({ data, isDraftPost }: HistorySectionProps) => {
  const { t, i18n } = useTranslation();
  const [showHistoryDraftIds, setShowHistoryDraftIds] = useState<number[]>([]);

  if (!data?.length) {
    return null;
  }

  const handleToggleDraftId = (draftPostId: number) => {
    const newIds = [...showHistoryDraftIds];

    if (newIds.includes(draftPostId)) {
      const index = newIds.findIndex((newId) => newId === draftPostId);
      newIds.splice(index, 1);
    } else {
      newIds.push(draftPostId);
    }

    setShowHistoryDraftIds(newIds);
  };

  const getCustomHistoryPostReason = ({ reason, status, operatedUserName }: MarketplacePostHistory) => {
    if (isDraftPost) {
      switch (status) {
        case MarketplaceCampaignPostHistoryStatus.DRAFT_REJECTED:
          return (
            <Trans
              components={{ b: <b /> }}
              i18nKey="Draft post was rejected by"
              values={{ username: operatedUserName }}
            />
          );
        case MarketplaceCampaignPostHistoryStatus.APPROVED:
          return (
            <Trans
              components={{ b: <b /> }}
              i18nKey="Draft post was approved by"
              values={{ username: operatedUserName }}
            />
          );
        case MarketplaceCampaignPostHistoryStatus.DRAFT:
          return t('Submitted a draft post');
        default:
          return reason;
      }
    } else {
      switch (status) {
        case MarketplaceCampaignPostHistoryStatus.CREATED:
          return t('The creator published the post');
        case MarketplaceCampaignPostHistoryStatus.APPROVED:
          return t('username approved the post', { username: operatedUserName });
        case MarketplaceCampaignPostHistoryStatus.REJECTED:
          return reason
            ? t('username rejected the post', { username: operatedUserName, reason })
            : t('username rejected the post without a reason', { username: operatedUserName });
        default:
          return reason;
      }
    }
  };

  return (
    <div className="flex-1 shrink-[auto] bg-black-10 p-6 pb-12">
      <h3 className="text-xl font-semibold">{t('History')}</h3>

      <div className="mt-4 grid grid-cols-[max-content_auto] gap-y-6">
        {data.map((history, index) => {
          const { id, operatedDate, detailedReason, draftPostContent, draftPostMaterials } = history;
          const showDraftHistoryPost = id && showHistoryDraftIds.includes(id);
          const reason = getCustomHistoryPostReason(history);

          return (
            <Fragment key={index}>
              <p className={styles.historyDate(index === 0)}>
                {localizedDateFormatter(operatedDate, 'PPP', i18n.language as AppLanguage)}
              </p>

              <div className="ml-12 flex-1 overflow-hidden">
                {reason ? <p className={styles.reason}>{reason}</p> : null}

                {detailedReason ? <LinkifyItPreview value={detailedReason} className="mt-2" /> : null}

                {history.status === MarketplaceCampaignPostHistoryStatus.DRAFT &&
                (draftPostMaterials?.length || draftPostContent) ? (
                  <>
                    <div
                      className={cn(styles.toggleDraftPostButton, { 'mt-2': !!detailedReason })}
                      onClick={() => (id ? handleToggleDraftId(id) : undefined)}
                    >
                      {t('Check the draft post')}
                      <Icon icon={showDraftHistoryPost ? 'caret-up' : 'caret-down'} size={8} />
                    </div>

                    {showDraftHistoryPost ? (
                      <div className="mt-2 w-full overflow-auto bg-white p-2">
                        {draftPostMaterials?.length ? (
                          <div className={styles.draftPostMaterialWrapper}>
                            {draftPostMaterials.map((image, idx) => (
                              <MediaPreview src={image} key={`material-${idx}`} className="h-17 w-17" />
                            ))}
                          </div>
                        ) : null}
                        {draftPostContent ? <p className={styles.draftPostContent}>{draftPostContent}</p> : null}
                      </div>
                    ) : null}
                  </>
                ) : null}
              </div>
            </Fragment>
          );
        })}
      </div>
    </div>
  );
};

const styles = {
  reason: cn('text-base leading-6'),
  historyDate: (firstItem: boolean) =>
    cn(
      'relative flex-none text-sm leading-6 whitespace-nowrap',
      firstItem
        ? 'before:absolute before:top-1.75 before:-right-6 before:z-2 before:flex before:h-2.5 before:w-2.5 before:flex-none before:translate-x-[calc(50%-0.5px)] before:transform before:items-center before:justify-center before:rounded-full before:bg-blue-30 before:pb-0.25 before:text-blue-70 before:content-["•"]'
        : 'before:absolute before:top-1.75 before:-right-6 before:z-1 before:h-2.5 before:w-2.5 before:flex-none before:translate-x-[calc(50%-0.5px)] before:transform before:rounded-full before:bg-black-40 before:content-[""]',
      'after:absolute after:top-2 after:-right-6 after:h-[calc(100%+1.5rem)] after:w-0.25 after:bg-black-30 after:content-[""]'
    ),
  draftPostContent: cn('mt-2 text-sm leading-[120%]'),
  draftPostMaterialWrapper: cn('flex h-17 overflow-auto'),
  toggleDraftPostButton: cn('flex cursor-pointer items-center gap-2 text-base leading-6 text-blue-70')
};
